import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { QUESTIONS, MILIEUS, SCORING, CLUSTERS } from '../data/quizData';
import { useChecklist } from '../contexts/ChecklistContext';

const Quiz = ({ onComplete }) => {
  const navigate = useNavigate();
  const { saveQuizResult } = useChecklist();
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [allAnswered, setAllAnswered] = useState(false);

  useEffect(() => {
    // Check if all questions are answered
    const answered = Object.keys(answers).length === QUESTIONS.length;
    setAllAnswered(answered);
  }, [answers]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const evaluateQuiz = () => {
    const scores = { KG: 0, PM: 0, PF: 0, EX: 0, NEO: 0, APM: 0, NB: 0, TR: 0, PR: 0, KH: 0 };
    const plus3 = { KG: 0, PM: 0, PF: 0, EX: 0, NEO: 0, APM: 0, NB: 0, TR: 0, PR: 0, KH: 0 };
    const plus2 = { KG: 0, PM: 0, PF: 0, EX: 0, NEO: 0, APM: 0, NB: 0, TR: 0, PR: 0, KH: 0 };

    for (const qid in answers) {
      const opt = answers[qid];
      const row = SCORING[qid]?.[opt] || {};
      for (const m in row) {
        const points = row[m];
        scores[m] += points;
        if (points === 3) plus3[m] += 1;
        if (points === 2) plus2[m] += 1;
      }
    }

    // Ranking: Sort by score, then +3 count, then +2 count, then alphabetically
    const entries = Object.keys(scores).map(k => ({
      code: k,
      name: MILIEUS[k].name,
      score: scores[k],
      plus3: plus3[k],
      plus2: plus2[k]
    }));
    entries.sort((a, b) => 
      b.score - a.score || 
      b.plus3 - a.plus3 || 
      b.plus2 - a.plus2 || 
      a.name.localeCompare(b.name)
    );

    const maxScore = entries[0].score;
    const top = entries.filter(e => e.score === maxScore);
    // Sort top entries by +3, then +2 for tie-breaking
    const topSorted = top.sort((a, b) => 
      b.plus3 - a.plus3 || 
      b.plus2 - a.plus2 || 
      a.name.localeCompare(b.name)
    );

    const primary = topSorted[0];
    // Ties: all entries with same score, same +3 count, and same +2 count (but not the primary)
    const ties = entries.filter(e => 
      e.score === primary.score && 
      e.code !== primary.code && 
      e.plus3 === primary.plus3 && 
      e.plus2 === primary.plus2
    );

    // Find the cluster for the primary milieu
    const findClusterForMilieu = (milieuName) => {
      for (const [clusterName, clusterData] of Object.entries(CLUSTERS)) {
        if (clusterData.milieus.includes(milieuName)) {
          return {
            name: clusterName,
            description: clusterData.description,
            milieus: clusterData.milieus
          };
        }
      }
      return null;
    };

    const userCluster = findClusterForMilieu(primary.name);

    const finalResult = {
      primary,
      ties,
      entries,
      maxScore,
      cluster: userCluster
    };
    
    setResult(finalResult);
    setShowResult(true);
    
    // Save result to context and localStorage
    saveQuizResult(finalResult);
    
    // Call onComplete if provided (for backward compatibility)
    if (onComplete) {
      onComplete(finalResult);
    }
  };

  const handleComplete = () => {
    // Navigate to quiz results page
    navigate('/quiz-results');
  };

  const handleReset = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setShowResult(false);
    setResult(null);
  };

  // Calculate progress based on answered questions, not current question position
  // This ensures 100% is only reached when all 12 questions are answered
  const answeredCount = Object.keys(answers).length;
  const progress = Math.min((answeredCount / QUESTIONS.length) * 100, 100);

  if (showResult && result) {
    // Automatically navigate to results page after a short delay
    // This gives time for the result to be saved
    setTimeout(() => {
      navigate('/quiz-results');
    }, 500);
    
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            Quiz abgeschlossen!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Du wirst zu deinen Ergebnissen weitergeleitet...
          </Typography>
        </Paper>
      </Container>
    );
  }

  const question = QUESTIONS[currentQuestion];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
            Welcher Sicherheitstyp bist Du?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
            Bitte beantworte uns kurz die folgenden 12 Fragen, damit wir die Inhalte optimal an deine <strong>persönliche Lebenswelt</strong> anpassen können.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
            Danach bekommst du deinen persönlichen Lernpfad zur Absicherung deines digtialen Alltags.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Wir speichern das Ergebnis, damit du weniger Zeit mit unpassenden Inhalten verbringst und nicht jedes Mal von vorne anfangen musst.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Frage {currentQuestion + 1} von {QUESTIONS.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(progress)}% abgeschlossen
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={progress} sx={{ mb: 3, height: 8, borderRadius: 4 }} />
        </Box>

        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              <Chip label={`Frage ${currentQuestion + 1}`} size="small" sx={{ mr: 2 }} />
              {question.q}
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              >
                {['A', 'B', 'C', 'D'].map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Chip 
                          label={option} 
                          size="small" 
                          sx={{ 
                            minWidth: 28, 
                            height: 28, 
                            fontWeight: 700,
                            bgcolor: 'grey.100',
                            color: 'text.primary'
                          }} 
                        />
                        <Typography variant="body1">
                          {question[option]}
                        </Typography>
                      </Box>
                    }
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'grey.50'
                      }
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button 
            variant="outlined" 
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
          >
            Zurück
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleReset}
            >
              Zurücksetzen
            </Button>
            
            {currentQuestion === QUESTIONS.length - 1 ? (
              <Button 
                variant="contained" 
                onClick={evaluateQuiz}
                disabled={!allAnswered}
                size="large"
              >
                Auswerten
              </Button>
            ) : (
              <Button 
                variant="contained" 
                onClick={handleNext}
                disabled={!answers[question.id]}
              >
                Weiter
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Quiz;
